import { getRelatedBoards } from "@/services";
import { useMemoizedFn } from "ahooks";
import { useCallback, useRef, useState } from "react";

/**
 * 同在画板信息接口
 */
export interface RelatedBoardInfo {
  board_id: number;
  title: string;
  user: {
    user_id: number;
    username: string;
    urlname: string;
  };
  pin_count: number;
  follow_count: number;
  like_count: number;
  is_private: number;
  category_name: string;
  seq: number;
}

/**
 * 扩展的 Pin 接口，包含同在画板信息
 */
export interface PinWithRelatedBoards extends IPin {
  relatedBoards?: RelatedBoardInfo[];
  relatedBoardsLoading?: boolean;
  relatedBoardsError?: boolean;
}

/**
 * 批量获取同在画板信息的 Hook
 */
export function useRelatedBoards() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestQueueRef = useRef<Set<number>>(new Set());
  const cacheRef = useRef<Map<number, RelatedBoardInfo[]>>(new Map());

  /**
   * 批量获取同在画板信息
   */
  const batchGetRelatedBoards = useMemoizedFn(
    async (pins: IPin[]): Promise<PinWithRelatedBoards[]> => {
      if (!pins.length) return pins as PinWithRelatedBoards[];

      // 过滤出需要获取同在画板信息的图片
      const pinsToProcess = pins.filter(
        (pin) =>
          pin.pin_id &&
          !cacheRef.current.has(pin.pin_id) &&
          !requestQueueRef.current.has(pin.pin_id)
      );

      if (!pinsToProcess.length) {
        // 如果都有缓存，直接返回带缓存数据的结果
        return pins.map((pin) => ({
          ...pin,
          relatedBoards: cacheRef.current.get(pin.pin_id) || [],
          relatedBoardsLoading: false,
          relatedBoardsError: false,
        })) as PinWithRelatedBoards[];
      }

      setLoading(true);
      setError(false);

      try {
        // 将需要处理的 pin_id 加入队列
        pinsToProcess.forEach((pin) => {
          requestQueueRef.current.add(pin.pin_id);
        });

        // 批量请求同在画板信息
        const promises = pinsToProcess.map(async (pin) => {
          try {
            const result = await getRelatedBoards({
              pin_id: pin.pin_id,
              max: 10,
              limit: 10, // 限制数量以提高性能
            });

            const relatedBoards = result?.boards || [];

            // 缓存结果
            cacheRef.current.set(pin.pin_id, relatedBoards);

            return {
              pin_id: pin.pin_id,
              relatedBoards,
              success: true,
            };
          } catch (err) {
            console.error(`获取图片 ${pin.pin_id} 的同在画板信息失败:`, err);
            return {
              pin_id: pin.pin_id,
              relatedBoards: [],
              success: false,
            };
          } finally {
            // 从队列中移除
            requestQueueRef.current.delete(pin.pin_id);
          }
        });

        const results = await Promise.all(promises);

        // 创建结果映射
        const resultMap = new Map<number, RelatedBoardInfo[]>();
        results.forEach((result) => {
          if (result.success) {
            resultMap.set(result.pin_id, result.relatedBoards);
          }
        });

        // 返回带同在画板信息的结果
        return pins.map((pin) => {
          const relatedBoards =
            resultMap.get(pin.pin_id) || cacheRef.current.get(pin.pin_id) || [];
          return {
            ...pin,
            relatedBoards,
            relatedBoardsLoading: false,
            relatedBoardsError:
              !resultMap.has(pin.pin_id) && !cacheRef.current.has(pin.pin_id),
          };
        }) as PinWithRelatedBoards[];
      } catch (err) {
        console.error("批量获取同在画板信息失败:", err);
        setError(true);

        // 返回原始数据，标记为错误状态
        return pins.map((pin) => ({
          ...pin,
          relatedBoards: [],
          relatedBoardsLoading: false,
          relatedBoardsError: true,
        })) as PinWithRelatedBoards[];
      } finally {
        setLoading(false);
      }
    }
  );

  /**
   * 获取单个图片的同在画板信息
   */
  const getSingleRelatedBoards = useMemoizedFn(
    async (pin: IPin): Promise<RelatedBoardInfo[]> => {
      if (!pin.pin_id) return [];

      // 检查缓存
      if (cacheRef.current.has(pin.pin_id)) {
        return cacheRef.current.get(pin.pin_id) || [];
      }

      // 检查是否正在请求
      if (requestQueueRef.current.has(pin.pin_id)) {
        return [];
      }

      try {
        requestQueueRef.current.add(pin.pin_id);

        // 使用与详情页一致的参数：limit: 20, max: 0
        const result = await getRelatedBoards({
          pin_id: pin.pin_id,
          max: 0,
          limit: 20,
        });

        const relatedBoards = result?.boards || [];
        cacheRef.current.set(pin.pin_id, relatedBoards);

        return relatedBoards;
      } catch (err) {
        console.error(`获取图片 ${pin.pin_id} 的同在画板信息失败:`, err);
        return [];
      } finally {
        requestQueueRef.current.delete(pin.pin_id);
      }
    }
  );

  /**
   * 清除缓存
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    requestQueueRef.current.clear();
  }, []);

  /**
   * 获取缓存状态
   */
  const getCacheStatus = useCallback(() => {
    return {
      cacheSize: cacheRef.current.size,
      queueSize: requestQueueRef.current.size,
      isLoading: loading,
      hasError: error,
    };
  }, [loading, error]);

  return {
    batchGetRelatedBoards,
    getSingleRelatedBoards,
    clearCache,
    getCacheStatus,
    loading,
    error,
  };
}
